import type { CamelToUpperSnake } from "@dresscode-frontend/utility-types";

import { camelToUpperSnake } from "./string";

/**
 * Enum Objectの型定義
 *
 * @template U Enumの値の型
 * @template T Enumの値の配列
 */
type EnumObject<U extends string, T extends readonly [U, ...U[]]> = {
  get: (index: number) => T[number];
  isMember: (value: string) => value is T[number];
  length: number;
  all: T;
} & { [K in T[number] as CamelToUpperSnake<K>]: K };

/**
 * Enum ObjectからUnion型を取得する
 * @example
 * const STATUS = createEnumObject(["inProgress", "completed"]);
 * type Status = EnumObjectToUnion<typeof STATUS>;
 * const status: Status = STATUS.IN_PROGRESS; // OK
 * const invalid: Status = "INVALID"; // Error
 */
export type EnumObjectToUnion<Enum extends object> = Enum[keyof Enum];

/**
 * 配列からEnum Objectを作成する
 * キーは自動的にアッパースネークケースに変換される
 *
 * @param values Enumの値の配列
 * @returns Enum Object
 * @example
 * const TAX_CALC_METHOD = createEnumObject(["exclusive", "inclusive"]);
 * console.log(TAX_CALC_METHOD.EXCLUSIVE); // "exclusive"
 * console.log(TAX_CALC_METHOD.INCLUSIVE); // "inclusive"
 *
 * const STATUS = createEnumObject(["inProgress", "completed"]);
 * console.log(STATUS.IN_PROGRESS); // "inProgress"
 * console.log(STATUS.COMPLETED); // "completed"
 */
export function createEnumObject<
  U extends string,
  T extends readonly [U, ...U[]],
>(values: T): EnumObject<U, T> {
  const base = {
    get: (index: number) => values[index],
    isMember: (value: string): value is T[number] =>
      (values as readonly string[]).includes(value),
    length: values.length,
    all: values,
  };

  const ret = values.reduce((acc, value) => {
    const key = camelToUpperSnake(value) as CamelToUpperSnake<typeof value>;
    return { ...acc, [key]: value };
  }, base) as EnumObject<U, T>;

  return ret;
}

/**
 * Enum Objectから全てのメンバーを取得する
 * @param obj Enum Object
 * @returns 全てのメンバー
 * @example
 * const TAX_CALC_METHOD = createEnum(["EXCLUSIVE", "INCLUSIVE"]);
 * console.log(getEnumMembers(TAX_CALC_METHOD)); // ["EXCLUSIVE", "INCLUSIVE"]
 */
export function getEnumMembers<Enum extends object>(obj: Enum) {
  return Object.values(obj).filter(
    (value) => typeof value === "string",
  ) as Enum[keyof Enum][];
}

const setMap = new WeakMap<object, Set<unknown>>(); // enum object -> set of values
/**
 * Enum Objectからメンバーかどうかを判定する
 * @param obj Enum Object
 * @param value 判定する値
 * @returns メンバーかどうか
 * @example
 * const TAX_CALC_METHOD = createEnum(["EXCLUSIVE", "INCLUSIVE"]);
 * console.log(isEnumMember(TAX_CALC_METHOD, "EXCLUSIVE")); // true
 * console.log(isEnumMember(TAX_CALC_METHOD, "INVALID")); // false
 */
export function isEnumMember<Enum extends object>(
  obj: Enum,
  value: unknown,
): value is Enum[keyof Enum] {
  let set = setMap.get(obj);
  if (!set) {
    console.log("WeakMap.prototype.set is being called");
    set = new Set(getEnumMembers(obj)) as Set<unknown>;
    setMap.set(obj, set);
  }
  return set.has(value);
}
